import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { AvEmptyStateComponent } from '../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvPaginatorComponent } from '../../../../Shared/Components/av-paginator/av-paginator.component';
import { AvStatusChipComponent } from '../../../../Shared/Components/av-status-chip/av-status-chip.component';
import { AvOrganizationPickerComponent } from '../../../../Shared/Components/av-organization-picker/av-organization-picker.component';
import { ReconciliationFacade } from '../../../../../Facades/Reconciliation/reconciliation.facade';
import { DATE_TIME_FORMAT, VALUE_UNRESOLVED } from '../../../../../Consts/backoffice.const';
import { SHAPE_EXPLANATION, SHAPE_LABEL } from '../../../../../Consts/reconciliation.const';
import {
  PRICE_COMPONENT_LABEL,
  PriceComponentType,
} from '../../../../../Enums/price-component-type.enum';

@Component({
  selector: 'app-reconciliation',
  standalone: true,
  imports: [
    DatePipe,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    AvEmptyStateComponent,
    AvPaginatorComponent,
    AvStatusChipComponent,
    AvOrganizationPickerComponent,
  ],
  templateUrl: './reconciliation.component.html',
  styleUrl: './reconciliation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class ReconciliationComponent {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);

  readonly facade = inject(ReconciliationFacade);

  readonly dateTimeFormat = DATE_TIME_FORMAT;
  readonly unresolved = VALUE_UNRESOLVED;

  private readonly _query = toSignal(this._route.queryParamMap);

  constructor() {
    this._registerOrganizationQueryParam();
  }

  shapeLabel(shape: string): string {
    return SHAPE_LABEL[shape] || shape;
  }

  shapeExplanation(shape: string): string {
    return SHAPE_EXPLANATION[shape] || '';
  }

  componentLabelKey(componentType: string): string {
    return PRICE_COMPONENT_LABEL[componentType as PriceComponentType] ?? componentType;
  }

  selectOrganization(organizationId: string): void {
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { organization: organizationId || null },
      queryParamsHandling: 'merge',
    });
  }

  runPortfolio(): void {
    this.facade.runPortfolio();
  }

  runForOrganization(): void {
    this.facade.runForOrganization();
  }

  goToReportPage(page: number): void {
    this.facade.goToReportPage(page);
  }

  changePageSize(pageSize: number): void {
    this.facade.changePageSize(pageSize);
  }

  reload(): void {
    this.facade.reload();
  }

  private _registerOrganizationQueryParam(): void {
    effect(() => {
      this.facade.selectOrganization(this._query()?.get('organization') ?? '');
    });
  }
}
